import { ActivityType, TaskStatus } from "@repo/api/client";
import {
	Button,
	Flex,
	Icon,
	Popover,
	Text,
	useTextStyles,
	useTheme,
} from "@repo/ui";
import { useState } from "react";
import { Dialog, DialogTrigger } from "react-aria-components";
import { toast } from "sonner";
import { useApiClient } from "../contexts/ApiContext";
import { useEntity, useQuery, useTransaction } from "../db/DatabaseProvider";
import { ActivityLogItemId, TaskId } from "../db/schema";
import { referralSentState } from "../referralSentState";

/** Today in the browser's timezone, as an `<input type="date">` value. */
function todayInputValue(): string {
	const now = new Date();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	return `${now.getFullYear()}-${month}-${day}`;
}

type SentStatusPickerProps = {
	taskId: TaskId;
};

/**
 * Whether the referral has gone out, and the only place staff can record a
 * send they made themselves.
 *
 * The mark goes through the API, not a sync mutation, because the server
 * decides from the task's current status whether the patient gets notified.
 * So the pill updates on the next sync pull, not optimistically.
 */
export function SentStatusPicker({ taskId }: SentStatusPickerProps) {
	const apiClient = useApiClient();
	const theme = useTheme();
	const dateTextClassName = useTextStyles({ size: "sm" });
	const [isOpen, setIsOpen] = useState(false);
	const [sentDate, setSentDate] = useState(todayInputValue);
	const [isSaving, setIsSaving] = useState(false);
	const task = useEntity("task", taskId);

	const sentItems = useQuery(
		"activityLogItem",
		(q) =>
			q
				.where("taskId", "=", taskId)
				.where("activityType", "=", ActivityType.ReferralSent),
		[taskId],
	);
	const state = referralSentState(sentItems);

	const removeLocally = useTransaction((tx, id: ActivityLogItemId) => {
		tx.remove("activityLogItem", id);
	});

	const undo = async (activityLogItemId: string) => {
		try {
			// The item may not have synced to this tab yet, so the server deletes
			// it; the local remove just updates the pill now when it has.
			const { deleted } = await apiClient.admin.undoReferralSentOutsideSkyway({
				activityLogItemId,
			});
			if (!deleted) {
				// Nothing to undo on the server, so the pill must not claim otherwise.
				toast.error("Couldn't undo marking this referral as sent");
				return;
			}
			removeLocally(activityLogItemId as ActivityLogItemId);
		} catch (error) {
			console.error("Failed to undo referral sent outside Skyway:", error);
			toast.error("Couldn't undo marking this referral as sent");
		}
	};

	const save = async () => {
		setIsSaving(true);
		try {
			const { activityLogItemId } =
				await apiClient.admin.markReferralSentOutsideSkyway({
					taskId,
					sentDate,
				});
			setIsOpen(false);
			toast("Marked as sent outside Skyway", {
				action: { label: "Undo", onClick: () => void undo(activityLogItemId) },
			});
		} catch (error) {
			console.error("Failed to mark referral sent outside Skyway:", error);
			toast.error("Couldn't mark this referral as sent");
		} finally {
			setIsSaving(false);
		}
	};

	// Only "not sent" has an action. A sent referral reads as plain status:
	// Skyway sends can't be changed here, and Undo lives on the toast.
	if (state !== "not_sent") {
		return (
			<Flex align="center" gap="xs" padding="xs">
				<Icon name="Send" size="sm" color="green10" />
				<Text size="sm" weight="medium" capsize>
					{state === "skyway" ? "Sent by Skyway" : "Sent outside Skyway"}
				</Text>
			</Flex>
		);
	}

	// Mirrors the server: only an Action Required task is woken, and only the
	// agent sends the referral-sent text.
	const notificationHint =
		task?.status === TaskStatus.Snoozed
			? "The task is snoozed, so the patient won't be texted."
			: task?.status === TaskStatus.Completed
				? "The task is completed, so the patient won't be texted."
				: task?.assigneeType === "agent"
					? "The agent will text the patient that their referral was sent."
					: "The patient won't be texted.";

	const fieldStyle = {
		padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
		border: "none",
		borderRadius: theme.borderRadiuses.md,
		boxShadow: theme.shadows.subtle,
		background: "transparent",
		color: theme.colors.sand12,
	};

	return (
		<DialogTrigger
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (open) setSentDate(todayInputValue());
				setIsOpen(open);
			}}
		>
			<Button size="sm" variant="ghost" icon="Send" iconColor="sand11">
				Not sent
			</Button>
			<Popover placement="bottom end">
				<Dialog
					aria-label="Mark as sent outside Skyway"
					style={{ outline: "none" }}
				>
					<Flex column gap="md" padding="md" style={{ width: 300 }}>
						<Flex column gap="xs">
							<Text size="md" weight="semibold">
								Mark as &ldquo;Sent outside Skyway&rdquo;
							</Text>
							<Text size="sm" color="sand11">
								{notificationHint}
							</Text>
						</Flex>
						<Flex column gap="xs">
							<Text size="xs" weight="medium" color="sand11">
								Date sent
							</Text>
							<input
								type="date"
								value={sentDate}
								max={todayInputValue()}
								onChange={(e) => setSentDate(e.target.value)}
								aria-label="Date sent"
								// A native input doesn't inherit the page font, so take the
								// design system's text styles, as TextField does.
								className={dateTextClassName}
								style={fieldStyle}
							/>
						</Flex>
						<Flex justify="flex-end" gap="xs">
							<Button
								size="sm"
								variant="ghost"
								onPress={() => setIsOpen(false)}
							>
								Cancel
							</Button>
							<Button
								size="sm"
								variant="primary"
								isDisabled={!sentDate || isSaving}
								onPress={() => void save()}
							>
								Save
							</Button>
						</Flex>
					</Flex>
				</Dialog>
			</Popover>
		</DialogTrigger>
	);
}
